import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { CustomValidationConfig, CustomValidationConstraint, CustomValidationError } from "go-form/services/types";
import { UpsellApi, UpsellVisibilityType } from "../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	form: UseFormReturn<UpsellApi>;
	setErrors: React.Dispatch<React.SetStateAction<CustomValidationError<any>[]>>;
	upsell: UpsellApi;
}

const UpsellFormBasic = ({ form, setErrors }: Props) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		watch,
		control,
	} = form;

	const textInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
			utils: { watch },
		}),
		[setErrors, watch]
	) satisfies CustomValidationConfig;

	const numberInputValidationConfig = useMemo(
		() => ({
			types: [{ constraint: CustomValidationConstraint.REQUIRED }],
			setErrors,
		}),
		[setErrors]
	) satisfies CustomValidationConfig;

	const visibilityTypeOptions = [
		{
			value: UpsellVisibilityType.ALL,
			label: t("enums.upsell.visibility_type.ALL"),
		},
		{
			value: UpsellVisibilityType.MISSING,
			label: t("enums.upsell.visibility_type.MISSING"),
		},
		{
			value: UpsellVisibilityType.NONE_IF_ANY_INCLUDED,
			label: t("enums.upsell.visibility_type.NONE_IF_ANY_INCLUDED"),
		},
	];

	return (
		<fieldset className="form-group no-gutters">
			<h5>{t("lib:common.word.basic_data")}</h5>
			<div className="row">
				<div className="col-md-6">
					<FormInput
						label={t("lib:common.word.name")}
						register={register}
						name="name"
						errors={errors}
						customValidationConfig={textInputValidationConfig}
					/>
				</div>
				<div className="col-md-6">
					<FormNumberInput
						errors={errors}
						control={control}
						name="priority"
						label={t("modules.upsell.field.priority.title")}
						suffix=""
						customValidationConfig={numberInputValidationConfig}
					/>
				</div>
				<div className="col-md-6">
					<FormSelectGroup
						errors={errors}
						control={control}
						name="visibility_type"
						label={t("modules.upsell.field.visibility_type.title")}
						options={visibilityTypeOptions}
						data-testid="visibility_type"
						isClearable={false}
					/>
				</div>
				<div className="col-md-6">
					<FormInput
						label={t("modules.upsell.field.label.title")}
						register={register}
						name="label"
						errors={errors}
					/>
				</div>
			</div>
		</fieldset>
	);
};

export default UpsellFormBasic;
